/**
 * studentShellAdapter — le compte connecté (`currentUser.object`) → ce que la coquille de l'élève
 * montre : son prénom et son avatar dans l'en-tête, ses compteurs, le point d'alerte, la tête de sa
 * feuille « moi ».
 *
 * Port littéral de l'en-tête legacy `StudentMainMenuHeader.js` : les compteurs n'existent que si le
 * compte les porte (`petals` absent = pas de gamification), et le point d'alerte compte un profil à
 * compléter et un e-mail à vérifier, jamais pour un élève sans formule (`getPrivilegedRole` →
 * `basic`, `data/utility/ability.js`).
 */
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { getPrivilegedRole } from "../../../../../data/utility/ability"
import { type UIProfileSwitcherMe, type UIStudentCounters } from "../../../ui/types"

/** Forme lâche : le compte vient d'un réducteur en JS, tous les champs sont optionnels. */
export type ApiStudentAccount = {
    id?: string | number
    firstname?: string
    display_name?: string
    avatar?: string | null
    roles?: string[]
    petals?: number
    xp?: number
    student_title?: string | null
    profile_completion?: number
    is_verified?: boolean
}

export type StudentShellAccount = {
    firstname: string
    avatarUri?: string
    /** Absent : un compte sans gamification — ni compteurs dans l'en-tête, ni titre dans la feuille. */
    counters: UIStudentCounters | null
    /** Profil à compléter ou e-mail à vérifier : le point sur l'avatar. */
    alert: boolean
    /**
     * La formule de l'élève (`getPrivilegedRole`), dont le pictogramme se pose sur l'avatar de
     * l'en-tête, comme aujourd'hui (`StudentMainMenuHeader.js:226-232`). `null` : sans formule.
     */
    offer: StudentOfferKey | null
    me: UIProfileSwitcherMe
}

/** Les formules dont l'ancien en-tête posait le pictogramme (`offersImages`, `PricingColumnM.js`). */
export type StudentOfferKey = "plus" | "premium" | "initial" | "essential" | "integral"

export function toStudentShellAccount(account: ApiStudentAccount | null | undefined): StudentShellAccount {
    const user = account ?? {}
    const firstname = user.firstname ?? user.display_name ?? ""
    const avatarUri = user.avatar ? cleanMediaUrl(user.avatar) : undefined
    // « Pétales, défis, XP » éteint dans son espace : un compte sans gamification.
    const counters: UIStudentCounters | null =
        user.petals === undefined || !isBlockOn(user, TENANT_BLOCKS.GAMIFICATION)
            ? null
            : { petals: user.petals, xp: user.xp, title: user.student_title ?? null }

    const pending =
        (user.profile_completion !== undefined && user.profile_completion < 100 ? 1 : 0) +
        (user.is_verified !== undefined && !user.is_verified ? 1 : 0)

    const role = getPrivilegedRole(user)
    return {
        firstname,
        avatarUri,
        counters,
        alert: pending > 0 && role !== "basic",
        offer: role === "basic" ? null : (role as StudentOfferKey),
        me: { name: firstname, avatarUri, title: counters?.title ?? null },
    }
}
